"use client";

// @ts-strict-ignore
import styled from "styled-components";
import { Row } from "components/layout/Grid";
import NextLink from "components/parts/NextLink";
import { Body, Scales } from "components/parts/Typography";
import NextImage from "components/parts/NextImage";

export const BlogArticlePageBackground = styled.div`
  background-color: ${({ theme }) => theme.backgroundColors.secondary};
`;

export const BlogArticlePageContainer = styled.main`
  padding: 74px 32px 24px;
  max-width: 784px;
  background-color: ${({ theme }) => theme.backgroundColors.primary};
  margin: 0 auto;
  ${({ theme }) => theme.media.desktop`
    padding: 80px 100px 24px;
  `}
`;

export const BlogHomepageLink = styled(NextLink)`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  width: max-content;
`;

export const ArrowLeftIcon = styled(NextImage).attrs({
  height: 20,
  width: 20
})``;

export const Divider = styled.hr`
  border-top: 1px solid ${({ theme }) => theme.borderColors.primary};
`;

export const CategoryAndPublishedDate = styled.div`
  display: flex;
`;

export const ArticleCategory = styled(Body).attrs({
  size: "md",
  semiBold: true,
  sm: Scales.scale2,
  color: "secondary"
})`
  margin-top: 16px;
  margin-bottom: 16px;
  &:hover {
    text-decoration: underline;
    color: unset;
  }
`;

export const ArticlePublishDate = styled(Body).attrs({
  size: "md",
  sm: Scales.scale2
})`
  margin-top: 16px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.textColors.secondary};
`;

export const BlogHeroImgContainer = styled.div`
  img {
    width: 100%;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.borderColors.primary};
    border-radius: 8px;
  }
`;

export const AuthorContainer = styled.div`
  display: flex;
  margin: 24px 0;
`;

export const AuthorImgContainer = styled.div`
  border-radius: 50%;
  margin-right: 16px;
`;

export const AuthorDetails = styled.div`
  justify-content: center;
  flex-direction: column;
  display: flex;
  height: 100%;
  &:hover {
    > p {
      text-decoration: underline;
      color: unset;
    }
  }
`;

export const AuthorNameOrTitle = styled(Body).attrs({
  size: "md",
  sm: Scales.scale2
})`
  margin: 0;
  color: ${({ theme }) => theme.textColors.secondary};
`;

export const BlogContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  ${({ theme: { media } }) => media.desktop`
    flex-direction: row;
  `}
`;

export const BlogContent = styled.div`
  margin: 0 0 80px;

  ${({ theme }) => theme.media.mobile`
    margin: 0 0 64px;
  `}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }

  p {
    margin: 0 0 24px 0;
    font-size: 16px;
    line-height: 24px;
  }

  a {
    word-break: break-word;
    color: inherit;
    text-decoration: underline;
  }

  ol li {
    margin: 16px 0;
    line-height: 24px;
  }

  ul li {
    margin: 16px 0;
    line-height: 24px;
  }

  .anchorBtn {
    color: white;
    background-color: black;
    border-radius: 999px;
    width: 180px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration-line: none;
    font-size: 18px;
    font-weight: 600;
  }

  .anchorBtn > :first-child {
    text-decoration-line: none;
  }

  blockquote {
    margin: 40px 0;
    position: relative;

    ${({ theme: { media } }) => media.tablet`
      margin: 80px 0;
    `}

    p {
      margin: 0;
      padding: 0 0 0 40px;
      font-size: ${({ theme }) => theme.fontSize.large}px;
      font-weight: ${({ theme }) => theme.fontSize.semiThin};
      line-height: ${({ theme }) => theme.lineHeight.medium};
      &::after {
        content: "";
        display: block;
        width: 25px;
        height: 46px;
        position: absolute;
        left: 0;
        top: -7px;
        background: url(/static/images/icon-quote.svg) no-repeat center center;
        background-size: contain;
      }

      ${({ theme: { media } }) => media.tablet`
        font-size: ${({ theme }) => theme.fontSize.extraLarge * 1.2}px;
      `}

      ${({ theme: { media } }) => media.desktop`
        font-size: ${({ theme }) => theme.fontSize.huge / 1.1}px;
        padding: 0;
          &::after {
            left: -75px;
            top: 6px;
            width: 50px;
          }
      `}
    }
  }

  img {
    width: auto;
    max-width: 100%;
    height: auto;
    object-fit: cover;
    margin: 50px 0 0;
  }

  video {
    width: auto;
    max-width: 100%;
    height: auto;
    object-fit: cover;
  }

  img + em {
    font-size: 14px;
    line-height: 1.43;
    color: ${({ theme }) => theme.textColors.secondary};
    text-align: center;
    font-style: normal;
    display: block;
  }

  pre {
    overflow: auto;
    background-color: ${({ theme }) => theme.backgroundColors.secondary};
    border-radius: 2px;
    padding: 8px;
  }

  em {
    color: ${({ theme }) => theme.textColors.secondary};
  }
`;

export const ArticleListContainer = styled(Row)`
  margin: 0;
  margin-bottom: 80px;
`;

export const ArticleRow = styled(Row)`
  margin-top: 24px;
  margin-bottom: 24px;
  width: 100%;
`;

export const MoreFromAuthorTitleBar = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
`;

export const SocialIconsContainer = styled.div`
  display: none;
  ${({ theme }) => theme.media.tablet`
    margin-top: 24px;
    margin-bottom: 32px;
    display: block;
  `}
  ${({ theme }) => theme.media.desktop`
    margin-top: 32px;
  `}
`;
